/* Owner-only dialog: email a cookbook invite code to a friend.
 * Modes:
 *   • viewer    — friend can view the cookbook
 *   • co_editor — friend can edit recipes inside it (REQUIRES paying member)
 *
 * The recipient gets an HTML email with the 10-digit code + a Dashboard link
 * pre-filled. On the receiving side, /api/cookbooks/redeem-code accepts the
 * kind and gates co_editor to subscription_tier != "free".
 */
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Eye, PencilSimple, PaperPlaneTilt, Copy } from "@phosphor-icons/react";

export default function ShareCookbookDialog({ open, onOpenChange, cookbook }) {
  const [email, setEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [note, setNote] = useState("");
  const [kind, setKind] = useState("viewer");
  const [busy, setBusy] = useState(false);

  const reset = () => { setEmail(""); setRecipientName(""); setNote(""); setKind("viewer"); };

  const send = async () => {
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes("@")) {
      toast.error("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/cookbooks/${cookbook.cookbook_id}/share-email`, {
        email: clean,
        recipient_name: recipientName.trim(),
        kind,
        note: note.trim(),
      });
      if (data.sent) {
        toast.success(`Invite emailed to ${clean}`);
      } else if (data.mock) {
        toast.success(`Invite prepared (email service not configured) — share code: ${data.code}`);
      } else {
        toast.success("Invite created — share the code below.");
      }
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send invite — try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!cookbook?.invite_code) return;
    await navigator.clipboard.writeText(cookbook.invite_code);
    toast.success("Code copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-rf-cream border-rf-ink/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-rf-ink">Invite a friend</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-sm text-rf-ink-soft">
            Email a 10-digit invite code so a friend can {kind === "co_editor" ? "co-edit" : "view"} <b>{cookbook?.title}</b>.
          </p>

          <div>
            <label className="overline text-rf-ink">Their access</label>
            <div className="grid grid-cols-2 gap-2 mt-2" data-testid="share-kind-toggle">
              <button
                type="button"
                onClick={() => setKind("viewer")}
                data-testid="share-kind-viewer"
                className={`flex items-start gap-3 text-left p-3 rounded-2xl border-2 transition-all ${kind === "viewer" ? "border-rf-orange bg-rf-amber/15" : "border-rf-ink/10 bg-white hover:border-rf-ink/30"}`}
              >
                <Eye size={20} weight="bold" className="text-rf-ink mt-0.5" />
                <div>
                  <p className="font-bold text-rf-ink text-sm">View-only</p>
                  <p className="text-[11px] text-rf-ink-soft leading-snug">Friend can read every recipe, but can&apos;t edit anything.</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setKind("co_editor")}
                data-testid="share-kind-coeditor"
                className={`flex items-start gap-3 text-left p-3 rounded-2xl border-2 transition-all ${kind === "co_editor" ? "border-rf-orange bg-rf-amber/15" : "border-rf-ink/10 bg-white hover:border-rf-ink/30"}`}
              >
                <PencilSimple size={20} weight="bold" className="text-rf-ink mt-0.5" />
                <div>
                  <p className="font-bold text-rf-ink text-sm">Co-editor</p>
                  <p className="text-[11px] text-rf-ink-soft leading-snug">Can add + edit recipes. Requires a paying-member account on their side.</p>
                </div>
              </button>
            </div>
            {kind === "co_editor" && (
              <p className="text-[11px] text-rf-orange mt-2" data-testid="share-coeditor-note">
                Co-editing is gated on the recipient&apos;s plan — they&apos;ll be prompted to upgrade if they&apos;re on the free tier.
              </p>
            )}
          </div>

          <div>
            <label className="overline text-rf-ink">Their email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="friend@example.com"
              data-testid="share-email-input"
              className="input-rf mt-1"
            />
          </div>

          <div>
            <label className="overline text-rf-ink">Their name (optional)</label>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Sam"
              data-testid="share-name-input"
              className="input-rf mt-1"
            />
          </div>

          <div>
            <label className="overline text-rf-ink">Personal note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Thought you'd love these family recipes — Mum's apple pie is on page 4!"
              rows={3}
              data-testid="share-note-input"
              className="input-rf mt-1 resize-none"
              maxLength={500}
            />
          </div>

          {cookbook?.invite_code && (
            <div className="bg-rf-cream-alt rounded-xl px-4 py-3">
              <div className="flex items-start justify-between gap-3 mb-1">
                <div className="min-w-0">
                  <p className="overline mb-0.5">View-only code</p>
                  <p className="font-mono text-rf-orange text-lg font-black tracking-wider truncate" data-testid="share-current-code">{cookbook.invite_code}</p>
                </div>
                <button
                  type="button"
                  onClick={copyCode}
                  data-testid="share-copy-code"
                  className="btn-outline-ink !py-2 !px-3 !text-xs"
                ><Copy size={14} /> Copy</button>
              </div>
              <p className="text-[11px] text-rf-ink-soft leading-snug">
                Anyone with this code can <b>view</b> only. The email invite you send above creates a separate single-use link tied to your friend&apos;s email — that&apos;s the only way to grant co-editor access.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => onOpenChange(false)} className="btn-outline-ink">Cancel</button>
            <button
              onClick={send}
              disabled={busy}
              data-testid="share-send-btn"
              className="btn-amber disabled:opacity-60"
            >
              <PaperPlaneTilt size={16} weight="bold" /> {busy ? "Sending…" : "Send invite"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
